import { useState } from 'react';

import { Checkbox } from '../../shared/ui/checkbox';
import { FieldLabel } from '../../shared/ui/field-label';
import { GroupLabel } from '../../shared/ui/group-label';
import { Hint } from '../../shared/ui/hint';
import { Input } from '../../shared/ui/input';
import { Modal } from '../../shared/ui/modal';
import { Note } from '../../shared/ui/note';
import { ApiError } from '../../shared/api/client';
import { SCOPE_OPTIONS, type TApiKeyScope } from '../../entities/api-key';
import type { TApiKeys } from './use-api-keys';

export interface IMintedKey {
  id: string;
  name: string;
  keyPrefix: string;
  plaintext: string;
}

interface INewKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  keys: TApiKeys;
  onMinted: (minted: IMintedKey) => void;
}

export const NewKeyDialog = ({ open, onOpenChange, keys, onMinted }: INewKeyDialogProps) => {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<TApiKeyScope[]>([]);
  const error = keys.create.error;

  const close = (next: boolean) => {
    if (!next) {
      setName('');
      setScopes([]);
      keys.create.reset();
    }
    onOpenChange(next);
  };

  const toggle = (scope: TApiKeyScope, on: boolean) =>
    setScopes((current) =>
      on ? [...current, scope] : current.filter((candidate) => candidate !== scope),
    );

  const submit = () =>
    keys.create.mutate(
      { name: name.trim(), ...(scopes.length > 0 ? { scopes } : {}) },
      {
        onSuccess: (created) => {
          onMinted({
            id: created.id,
            name: created.name,
            keyPrefix: created.keyPrefix,
            plaintext: created.plaintext,
          });
          // Required: reset drops the mutation's `data`, the only place the plaintext secret lives.
          keys.create.reset();
          close(false);
        },
      },
    );

  return (
    <Modal
      open={open}
      onOpenChange={close}
      width="md"
      title="New key"
      description="You see the key once, right after you create it. Copy it then — it can’t be shown again."
      submit={{
        label: 'Create key',
        onSubmit: submit,
        disabled: name.trim().length === 0 || keys.create.isPending,
        waiting: keys.create.isPending,
        waitingLabel: 'Creating',
      }}
    >
      <div className="flex flex-col gap-4 pt-2">
        <div>
          <FieldLabel htmlFor="new-key-name">Name</FieldLabel>
          <Input
            id="new-key-name"
            autoFocus
            value={name}
            placeholder="CI pipeline"
            onChange={(event) => setName(event.target.value)}
          />
          <Hint>Say where it’s used, so you know which one to revoke later.</Hint>
        </div>

        <div>
          <GroupLabel>What it may do</GroupLabel>
          <div className="flex flex-col gap-2">
            {SCOPE_OPTIONS.map((scope) => (
              <Checkbox
                key={scope.value}
                checked={scopes.includes(scope.value)}
                onCheckedChange={(next) => toggle(scope.value, next)}
                label={
                  <span className="text-meta">
                    {scope.label}
                    {scope.implies && <span className="ml-2 text-faint">{scope.implies}</span>}
                  </span>
                }
              />
            ))}
          </div>
          <Hint>Tick nothing to let it do everything, including anything added later.</Hint>
        </div>

        {error && (
          <Note tone="danger">
            {error instanceof ApiError ? error.message : 'The key was not created.'}
          </Note>
        )}
      </div>
    </Modal>
  );
};
